import { useCallback, useEffect, useState } from 'react';

// Column model for the Operating Pressure table. Bespoke columns (a wrapping
// description, badge cells, a source link) get their own ordered / sized /
// visible config, persisted per panel. Mirrors the shape the shared ColumnsMenu
// / ColumnHeaderCell expect (same as referrals-columns).

export const PRESSURE_COLUMN_IDS = [
  'date',
  'description',
  'countries',
  'subsegments',
  'category',
  'severity',
  'direction',
  'source',
] as const;
export type PressureColumnId = (typeof PRESSURE_COLUMN_IDS)[number];

interface PressureColumnDef {
  label: string;
  defaultWidth: number;
  numeric: boolean;
}

export const PRESSURE_COLUMN_DEFS: Record<PressureColumnId, PressureColumnDef> = {
  date: { label: 'Date', defaultWidth: 64, numeric: false },
  description: { label: 'Description', defaultWidth: 360, numeric: false },
  countries: { label: 'Countries', defaultWidth: 84, numeric: false },
  subsegments: { label: 'Sub-segments', defaultWidth: 150, numeric: false },
  category: { label: 'Category', defaultWidth: 110, numeric: false },
  severity: { label: 'Severity', defaultWidth: 92, numeric: false },
  direction: { label: 'Direction', defaultWidth: 96, numeric: false },
  source: { label: 'Source', defaultWidth: 110, numeric: false },
};

const MIN_WIDTH = 44;
const SCHEMA_VERSION = 1;

export interface PressureColumnState {
  id: PressureColumnId;
  width: number;
  visible: boolean;
}

export function defaultPressureColumnState(): PressureColumnState[] {
  return PRESSURE_COLUMN_IDS.map((id) => ({
    id,
    width: PRESSURE_COLUMN_DEFS[id].defaultWidth,
    visible: true,
  }));
}

export interface PressureColumnConfig {
  columns: PressureColumnState[];
}

export interface PressureColumnActions {
  setColumnWidth: (id: PressureColumnId, px: number) => void;
  setColumnVisible: (id: PressureColumnId, visible: boolean) => void;
  moveColumn: (id: PressureColumnId, targetIndex: number) => void;
  resetToDefaults: () => void;
}

export function usePressureColumns(
  storageKey: string,
): [PressureColumnConfig, PressureColumnActions] {
  const [config, setConfig] = useState<PressureColumnConfig>(() => load(storageKey));

  useEffect(() => {
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({ v: SCHEMA_VERSION, columns: config.columns }),
      );
    } catch {
      /* non-critical */
    }
  }, [config, storageKey]);

  const setColumnWidth = useCallback((id: PressureColumnId, px: number) => {
    const width = Math.max(MIN_WIDTH, Math.round(px));
    setConfig((c) => ({
      columns: c.columns.map((col) => (col.id === id ? { ...col, width } : col)),
    }));
  }, []);

  const setColumnVisible = useCallback((id: PressureColumnId, visible: boolean) => {
    setConfig((c) => ({
      columns: c.columns.map((col) => (col.id === id ? { ...col, visible } : col)),
    }));
  }, []);

  const moveColumn = useCallback((id: PressureColumnId, targetIndex: number) => {
    setConfig((c) => {
      const fromIndex = c.columns.findIndex((col) => col.id === id);
      if (fromIndex === -1) return c;
      const clamped = Math.max(0, Math.min(c.columns.length - 1, targetIndex));
      if (fromIndex === clamped) return c;
      const next = [...c.columns];
      const [moved] = next.splice(fromIndex, 1);
      if (!moved) return c;
      next.splice(clamped, 0, moved);
      return { columns: next };
    });
  }, []);

  const resetToDefaults = useCallback(() => {
    setConfig({ columns: defaultPressureColumnState() });
  }, []);

  return [config, { setColumnWidth, setColumnVisible, moveColumn, resetToDefaults }];
}

function load(storageKey: string): PressureColumnConfig {
  const defaults: PressureColumnConfig = { columns: defaultPressureColumnState() };
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as { v?: number; columns?: unknown };
    if (parsed.v !== SCHEMA_VERSION || !Array.isArray(parsed.columns)) return defaults;
    const known = new Set<PressureColumnId>(PRESSURE_COLUMN_IDS);
    const stored = (parsed.columns as unknown[]).filter(
      (c): c is PressureColumnState =>
        typeof c === 'object' &&
        c !== null &&
        known.has((c as PressureColumnState).id) &&
        typeof (c as PressureColumnState).width === 'number' &&
        typeof (c as PressureColumnState).visible === 'boolean',
    );
    const seen = new Set(stored.map((c) => c.id));
    const missing = PRESSURE_COLUMN_IDS.filter((id) => !seen.has(id)).map((id) => ({
      id,
      width: PRESSURE_COLUMN_DEFS[id].defaultWidth,
      visible: true,
    }));
    return { columns: [...stored, ...missing] };
  } catch {
    return defaults;
  }
}
